
import { useToast } from '@/components/ui/use-toast';
import { getTrackDetails, isValidSpotifyUrl } from '@/lib/spotify';
import { SpotifyTrack } from '@/lib/types';

/**
 * Validates a track upload request
 */
export function validateUploadRequest(
  spotifyUrl: string,
  selectedFile: File | null,
  artistId: string | null,
  validateSpotifyUrl: (url: string) => boolean,
  toast: ReturnType<typeof useToast>['toast']
): boolean {
  if (!spotifyUrl || !validateSpotifyUrl(spotifyUrl)) {
    toast({
      title: "Invalid Spotify URL",
      description: "Please enter a valid Spotify track URL",
      variant: "destructive"
    });
    return false;
  }

  if (!selectedFile) {
    toast({
      title: "No file selected",
      description: "Please select an audio file to upload",
      variant: "destructive"
    });
    return false;
  }

  if (!artistId) {
    toast({
      title: "Authentication required",
      description: "You must be logged in to upload tracks",
      variant: "destructive"
    });
    return false;
  }

  return true;
}

/**
 * Gets track details from Spotify if not already available
 * Modified to avoid API call if trackPreview exists
 */
export async function getTrackDetailsIfNeeded(
  spotifyUrl: string,
  trackPreview: SpotifyTrack | null,
  toast: ReturnType<typeof useToast>['toast']
): Promise<SpotifyTrack | null> {
  // If we already have track details, use them and skip the API call
  if (trackPreview) {
    return trackPreview;
  }

  // Extract the track ID from the URL for embedding
  const trackId = spotifyUrl.split('/').pop()?.split('?')[0];
  if (!trackId) {
    return null;
  }

  // Return minimal track details without calling the API
  return {
    id: trackId,
    name: "Track " + trackId.substring(0, 6),
    artist: "Unknown Artist",
    albumCover: "",
    spotifyUrl: spotifyUrl
  };
}
